// src/containers/preview/index.js
//
// Home page ▸ "Learn More About Our Tools" — a hands-on demo class in the
// same design as the real subject pages (DynamicPortalPage + subjectPage.css):
//
//   • topic cards with real Video, Song, Sing-along and Game buttons
//   • 🎨 Decorate my avatar — the real avatar maker, nothing saved
//   • 👩‍🏫 Meet the class — made-up students' scores and every Q & A
//   • 🏆 Class leaderboard — made-up students, plus "You" once you earn coins
//   • 🎛️ Educator controls — hide/show topics on this demo page
//
// Content: publicDemo/home (published from Layout Builder ▸ 🏠 Home page
// demo) or, until then, the built-in sample topics in demoContent.js.
// Every click and play is counted for the admin tracker (services/
// demoTracker.js → trackDemo Cloud Function). Nothing a visitor does here is
// saved anywhere — progress, coins and the avatar last until they leave.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AvatarWithOutfit from "../../components/avatar/AvatarWithOutfit";
import AvatarMaker from "../../components/avatar/AvatarMaker";
import { DEFAULT_LOOK } from "../../components/avatar/avatarParts";
import { Coin } from "../../components/coins/CoinCounter";
import FunPopup from "../../components/common/FunPopup";
import CustomMaterialForm from "../../components/customMaterial/CustomMaterialForm";
import ModalPortal from "../../components/common/ModalPortal";
import { loadPublicDemo, questionsFor, trackDemo } from "../../services/demoTracker";
import { DEMO_FALLBACK_TOPICS, DEMO_SUBJECTS, DEMO_TEACHER } from "./demoContent";
import DemoMediaPopup from "./DemoMediaPopup";
import DemoGame from "./DemoGame";
import DemoClassPopup from "./DemoClassPopup";
import DemoLeaderboard from "./DemoLeaderboard";
import "../../pages/builder/subjectPage.css";
import "../../components/coins/coins.css";
import "../../components/leaderboard/leaderboard.css";
import "../../components/shop/shop.css";
import "../../styles/sbl-ui.css";
import "./preview.scss";
import "./demoPreview.css";

const LOGO =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";

// Demo coins — the same idea as the portal, kept small and simple.
const COINS = { video: 5, music: 5, singalong: 5, gameBase: 10, perRight: 2 };

const FILTERS = [
  { id: "all", label: "All", icon: "🎒" },
  { id: "science", ...DEMO_SUBJECTS.science },
  { id: "socialstudies", ...DEMO_SUBJECTS.socialstudies },
  { id: "math", ...DEMO_SUBJECTS.math },
];

const NO_ITEMS = {};

const scrollToSignup = () => {
  const el = document.getElementById("signup");
  if (!el) return;
  const header = document.querySelector(".header-sticky");
  const offset = (header ? header.offsetHeight : 90) + 10;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - offset, behavior: "smooth" });
};

export default function Preview() {
  const sectionRef = useRef(null);

  const [topics, setTopics] = useState(DEMO_FALLBACK_TOPICS);
  const [filter, setFilter] = useState("all");
  const [hidden, setHidden] = useState({}); // slug → true (demo Educator Controls)
  const [done, setDone] = useState({}); // slug → { video, music, singalong, game, score }
  const [coins, setCoins] = useState(0);
  const [myLook, setMyLook] = useState(DEFAULT_LOOK);

  const [media, setMedia] = useState(null); // { type, topic }
  const [game, setGame] = useState(null); // { topic, questions, gameType, placeholderImage }
  const [loadingGame, setLoadingGame] = useState("");
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [classOpen, setClassOpen] = useState(false);
  const [classTopics, setClassTopics] = useState(null); // topics with questions loaded
  const [boardOpen, setBoardOpen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [controlsDraft, setControlsDraft] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [pop, setPop] = useState(null);

  /* ---------------- content ---------------- */

  useEffect(() => {
    let alive = true;
    loadPublicDemo().then((published) => {
      if (alive && published?.topics?.length) setTopics(published.topics);
    });
    return () => {
      alive = false;
    };
  }, []);

  // "view": the visitor actually scrolled to the demo (counted once per page load)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          trackDemo("view");
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const visible = useMemo(() => topics.filter((t) => !hidden[t.slug]), [topics, hidden]);
  const shown = useMemo(() => (filter === "all" ? visible : visible.filter((t) => t.subject === filter)), [visible, filter]);
  const accent = filter === "all" ? "demo" : filter;

  // A game button only when there is something to play: published questions
  // or a static-library quiz for the built-in topics.
  const hasGame = (t) => (Array.isArray(t.questions) && t.questions.length > 0) || Boolean(t.quizSlug);

  const stepsOf = (t) =>
    [
      t.videoUrl && { key: "video", icon: "🎬", label: "Video" },
      t.musicUrl && { key: "music", icon: "🎵", label: "Song" },
      // the sing-along video, or the song with timed lyrics when there's no video
      (t.singAlongUrl || (t.sync && t.musicUrl)) && { key: "singalong", icon: "🎤", label: "Sing-along" },
      hasGame(t) && { key: "game", icon: "🎮", label: "Game" },
    ].filter(Boolean);

  const finishedCount = visible.filter((t) => {
    const st = stepsOf(t);
    return st.length > 0 && st.every((s) => done[t.slug]?.[s.key]);
  }).length;

  /* ---------------- progress (this visit only) ---------------- */

  // a mirror of `done` so a step is only ever paid once, without side
  // effects inside a state updater
  const doneRef = useRef({});
  const patchDone = useCallback((slug, patch) => {
    doneRef.current = { ...doneRef.current, [slug]: { ...(doneRef.current[slug] || {}), ...patch } };
    setDone(doneRef.current);
  }, []);

  // Media pays quietly (the popup says so itself); a finished game shows a
  // popup, because the visitor is back on the page by then.
  const markMedia = useCallback(
    (type, topic) => {
      if (doneRef.current[topic.slug]?.[type]) return 0;
      patchDone(topic.slug, { [type]: true });
      setCoins((c) => c + (COINS[type] || 0));
      return COINS[type] || 0;
    },
    [patchDone]
  );

  /* ---------------- buttons ---------------- */

  const openStep = async (key, topic) => {
    trackDemo(key, { slug: topic.slug, title: topic.title });
    if (key !== "game") {
      setMedia({ type: key, topic });
      return;
    }
    setLoadingGame(topic.slug);
    const q = await questionsFor(topic);
    setLoadingGame("");
    if (!q.questions.length) {
      setPop({ tone: "info", icon: "🎮", title: "Game coming soon", text: "This topic's game isn't in the demo yet — try another topic's game!" });
      return;
    }
    setGame({ topic, ...q });
  };

  const finishGame = ({ topic, quizScore, bonusScore, right, total }) => {
    const score = quizScore + bonusScore;
    trackDemo("game_finish", { slug: topic.slug, title: topic.title, n: score });
    const first = !doneRef.current[topic.slug]?.game;
    patchDone(topic.slug, { game: true, score: Math.max(score, doneRef.current[topic.slug]?.score || 0) });
    const earned = COINS.gameBase + right * COINS.perRight;
    setCoins((c) => c + earned);
    setPop({
      tone: "ok",
      icon: "🪙",
      title: `+${earned} coins!`,
      text: `${total ? `${right} of ${total} right in ${topic.title}` : `You finished ${topic.title}`}${first ? " — you earned its ⭐ game star!" : "!"} Check the 🏆 leaderboard.`,
    });
  };

  const signup = () => {
    trackDemo("signup");
    setMedia(null);
    setGame(null);
    setClassOpen(false);
    setBoardOpen(false);
    setAvatarOpen(false);
    setTimeout(scrollToSignup, 60);
  };

  const openClass = async () => {
    trackDemo("classmates");
    setClassOpen(true);
    if (classTopics) return;
    const withQuestions = await Promise.all(
      topics.map(async (t) => {
        const q = await questionsFor(t);
        return { ...t, questions: q.questions };
      })
    );
    setClassTopics(withQuestions);
  };

  const openControls = () => {
    trackDemo("controls");
    setControlsDraft(Object.fromEntries(topics.map((t) => [t.slug, !hidden[t.slug]])));
    setControlsOpen(true);
  };

  const saveControls = () => {
    const nextHidden = Object.fromEntries(Object.entries(controlsDraft).filter(([, on]) => !on).map(([slug]) => [slug, true]));
    setHidden(nextHidden);
    setControlsOpen(false);
    const n = Object.keys(nextHidden).length;
    setPop({
      tone: "ok",
      icon: "🎛️",
      title: "Controls saved!",
      text: n ? `${n} topic${n === 1 ? " is" : "s are"} now hidden — in your class, students' pages change right away.` : "Every topic is showing. In your class, students' pages change right away.",
    });
  };

  /* ---------------- render ---------------- */

  return (
    <section className="preview-wrapper--public gradient-color" id="preview" ref={sectionRef}>
      <div className="container-narrow pv-demo">
        <div className="preview-header" role="group" aria-labelledby="preview-title">
          <span className="eyebrow">Learn More About Our Tools</span>
          <h2 id="preview-title" className="h1" style={{ color: "#fff" }}>
            Try a real class — no sign-up needed
          </h2>
          <p className="lede" style={{ color: "#fff" }}>
            This is the same page students and teachers use. Watch a <strong>Video</strong>, play a <strong>Song</strong> or <strong>Sing-along</strong>, then play the
            topic's real <strong>Game</strong>. Decorate an avatar, meet a sample class to see the scores and answers a teacher sees, and check the class{" "}
            <strong>Leaderboard</strong>. Nothing here is saved.
          </p>
        </div>

        {/* ---------- top card: same design as the subject pages ---------- */}
        <div className={`sp-hero is-${accent} pv-hero`}>
          <div className="sp-hero-top">
            <img src={LOGO} alt="Smart Board Learning" className="sp-logo" />
            <div className="pv-filters" role="tablist" aria-label="Subjects">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={`pv-filter is-${f.id} ${filter === f.id ? "is-on" : ""}`} onClick={() => setFilter(f.id)}>
                  <span aria-hidden="true">{f.icon}</span> {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sp-hero-main">
            <button
              type="button"
              className="sp-hero-avatar"
              onClick={() => {
                trackDemo("avatar");
                setAvatarOpen(true);
              }}
              title="Decorate my avatar"
            >
              <AvatarWithOutfit look={myLook} size={104} items={NO_ITEMS} />
              <span className="sp-hero-edit">✎ Decorate me</span>
            </button>
            <div className="sp-hero-text">
              <p className="sp-kicker">Free demo · sample topics from different grades</p>
              <h1 className="sp-title">
                <span aria-hidden="true">{filter === "all" ? "🎒" : DEMO_SUBJECTS[filter].icon}</span> {filter === "all" ? "Demo Class" : DEMO_SUBJECTS[filter].label}
              </h1>
              <p className="sp-welcome">Welcome to {DEMO_TEACHER.name}'s class!</p>
              {visible.length > 0 && (
                <div className="sp-progress" title={`${finishedCount} of ${visible.length} topics finished`}>
                  <div className="sp-progress-bar">
                    <i style={{ width: `${Math.round((finishedCount / visible.length) * 100)}%` }} />
                  </div>
                  <span>
                    <b>{finishedCount}</b> of <b>{visible.length}</b> topics finished {finishedCount === visible.length && finishedCount > 0 ? "🏆" : ""}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="sp-hero-actions">
            <button
              type="button"
              className="sbl-shop-open"
              onClick={() => {
                trackDemo("avatar");
                setAvatarOpen(true);
              }}
            >
              <span aria-hidden="true">🎨</span> Decorate my avatar
            </button>
            <span className="sbl-coins sbl-coins-md" title="Demo coins — not saved">
              <Coin size={26} />
              <span className="sbl-coins-n">{coins}</span>
              <span className="sbl-coins-label">coins</span>
            </span>
            <button type="button" className="pv-act is-class" onClick={openClass}>
              <span aria-hidden="true">👩‍🏫</span> Meet the class
            </button>
            <button
              type="button"
              className="sbl-lb-open"
              onClick={() => {
                trackDemo("leaderboard");
                setBoardOpen(true);
              }}
            >
              <span aria-hidden="true">🏆</span> Class leaderboard
            </button>
            <button type="button" className="pv-act is-controls" onClick={openControls}>
              <span aria-hidden="true">🎛️</span> Educator controls
            </button>
          </div>
        </div>

        <p className="sp-howto pv-howto">For each topic: 🎬 watch, 🎵 listen, 🎤 sing along, then 🎮 play the game to earn its star!</p>

        {/* ---------- topic cards ---------- */}
        <div className="sp-grid pv-grid">
          {shown.length === 0 && (
            <div className="pv-empty">
              <p>No {filter === "all" ? "" : DEMO_SUBJECTS[filter]?.label} topics showing — open 🎛️ Educator controls to bring them back.</p>
            </div>
          )}
          {shown.map((topic, index) => {
            const pr = done[topic.slug] || {};
            const steps = stepsOf(topic);
            const n = steps.filter((s) => pr[s.key]).length;
            const complete = steps.length > 0 && n === steps.length;
            const subj = DEMO_SUBJECTS[topic.subject] || DEMO_SUBJECTS.science;
            return (
              <article key={topic.slug} className={`sp-card${complete ? " is-done" : ""}`} style={{ "--h": (index * 47 + 200) % 360 }}>
                <div className="sp-card-art">
                  {topic.imageUrl ? <img src={topic.imageUrl} alt="" loading="lazy" /> : <span className="sp-card-emoji" aria-hidden="true">{subj.icon}</span>}
                  <span className="sp-card-num">
                    {subj.label}
                    {topic.grade ? ` · ${topic.grade} grade` : ""}
                  </span>
                  {complete && <span className="sp-card-ribbon">⭐ Complete!</span>}
                  {pr.game && !complete && <span className="sp-card-ribbon is-star">⭐</span>}
                </div>
                <div className="sp-card-body">
                  <h3 className="sp-card-title">{topic.title}</h3>
                  {(topic.standardNumber || topic.description) && (
                    <p className="sp-card-meta">
                      {topic.standardNumber && <span className="sp-pill">{topic.standardNumber}</span>}
                      {topic.description}
                    </p>
                  )}
                  <div className="sp-steps">
                    {steps.map((st) => (
                      <button
                        key={st.key}
                        type="button"
                        className={`sp-step is-${st.key}${pr[st.key] ? " is-done" : ""}`}
                        onClick={() => openStep(st.key, topic)}
                        disabled={loadingGame === topic.slug && st.key === "game"}
                        title={pr[st.key] ? `${st.label} — done ✓${st.key === "game" && pr.score ? ` (score ${pr.score})` : ""}` : st.label}
                      >
                        <span className="sp-step-icon" aria-hidden="true">
                          {st.icon}
                        </span>
                        <span className="sp-step-label">{loadingGame === topic.slug && st.key === "game" ? "Loading…" : st.label}</span>
                        {pr[st.key] && (
                          <span className="sp-step-check" aria-label="done">
                            ✓
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  {steps.length > 0 && (
                    <div className="sp-card-progress">
                      <div className="sp-card-bar">
                        <i style={{ width: `${Math.round((n / steps.length) * 100)}%` }} />
                      </div>
                      <span>
                        {n}/{steps.length}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* ---------- teacher tools, as on the educator subject page ---------- */}
        <div className="pv-tools">
          <div className="pv-tool">
            <button type="button" className="sbl-tile sbl-tile-educator pv-tile" onClick={openControls}>
              <span className="sbl-tile-icon" aria-hidden="true">🎛️</span>
              <span className="sbl-tile-label">Educator Controls</span>
            </button>
            <p>
              Teachers choose which topics students see. Any student sharing the same Educator Code gets a portal controlled by that teacher. <b>Try it:</b> hide a topic above.
            </p>
          </div>
          <div className="pv-tool">
            <button
              type="button"
              className="sbl-tile sbl-tile-home pv-tile"
              onClick={() => {
                trackDemo("custom");
                setCustomOpen(true);
              }}
            >
              <span className="sbl-tile-icon" aria-hidden="true">✏️</span>
              <span className="sbl-tile-label">Request Custom Material</span>
            </button>
            <p>Send us your facts and we turn them into a song in the style you choose, plus a game to help students memorize exactly what they need.</p>
          </div>
          <div className="pv-tool">
            <button type="button" className="sbl-tile sbl-tile-classroom pv-tile" onClick={openClass}>
              <span className="sbl-tile-icon" aria-hidden="true">👩‍🏫</span>
              <span className="sbl-tile-label">View Students</span>
            </button>
            <p>See every student on your Educator Code: video and song plays, game scores, and each question they answered. <b>Try it</b> with our sample class.</p>
          </div>
        </div>

        <div className="pv-signup">
          <p>
            <b>Like what you see?</b> Your class gets every topic for your state and grade — and avatars, coins and progress are saved.
          </p>
          <button type="button" className="pvd-cta is-big" onClick={signup}>
            ✨ Start my free trial
          </button>
        </div>
      </div>

      {/* ---------- popups ---------- */}
      {media && <DemoMediaPopup type={media.type} topic={media.topic} onClose={() => setMedia(null)} onDone={markMedia} />}

      {game && (
        <DemoGame
          topic={game.topic}
          questions={game.questions}
          gameType={game.gameType}
          placeholderImage={game.placeholderImage}
          onClose={() => setGame(null)}
          onFinish={finishGame}
          onSignup={signup}
        />
      )}

      {avatarOpen && (
        <AvatarMaker
          demo
          startLook={myLook}
          onClose={() => setAvatarOpen(false)}
          onSaved={(look) => {
            trackDemo("avatar_save");
            setMyLook(look);
          }}
        />
      )}

      {classOpen && <DemoClassPopup topics={classTopics || []} loading={!classTopics} onClose={() => setClassOpen(false)} onSignup={signup} />}

      {boardOpen && <DemoLeaderboard myCoins={coins} myLook={myLook} onClose={() => setBoardOpen(false)} onSignup={signup} />}

      {controlsOpen && (
        <ModalPortal>
          <div className="pvd-pop" role="dialog" aria-modal="true" aria-label="Educator Controls" onMouseDown={(e) => e.target === e.currentTarget && setControlsOpen(false)}>
            <div className="pvd-box">
              <header className="pvd-head">
                <h3>🎛️ Educator Controls</h3>
                <button type="button" className="pvd-x" onClick={() => setControlsOpen(false)} aria-label="Close">
                  ✕
                </button>
              </header>
              <p className="pvd-sub">Tick the topics you want students to see. (Demo: this changes the cards on this page.)</p>
              <div className="pvd-controls">
                {topics.map((t) => (
                  <label key={t.slug} className="pvd-control">
                    <input type="checkbox" checked={controlsDraft[t.slug] !== false} onChange={() => setControlsDraft((d) => ({ ...d, [t.slug]: d[t.slug] === false }))} />
                    <span>{t.title}</span>
                    <small>{DEMO_SUBJECTS[t.subject]?.label}</small>
                  </label>
                ))}
              </div>
              <div className="pvd-box-foot">
                <button type="button" className="pvd-cta" onClick={saveControls}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      <CustomMaterialForm open={customOpen} onClose={() => setCustomOpen(false)} mode="demo" from="home-demo" />

      <FunPopup pop={pop} onClose={() => setPop(null)} />
    </section>
  );
}
